import { useState } from "react";
import { Loader2 } from "lucide-react";
import { FileTypeIcon } from "./fileTypeIcon";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DocumentBreadcrumbHover } from "@/domains/pm/components/issues/DocumentBreadcrumbHover";
import { useDocBreadcrumbs } from "./DocBreadcrumbsContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface DocChipProps {
  /** The filename to display */
  filename: string;
  /** Click handler to open the document preview. Omit it when the document
   *  can't be opened (a `.dwg` without the Drawings grant, a `.zip`, a file
   *  mid-conversion) — the chip then renders as plain text with no "click to
   *  open" promise. */
  onClick?: () => void;
  /** Whether the chip is in a loading state */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
  /** When false, render the chip without its built-in "Click to open preview"
   *  tooltip. Used where an outer wrapper already supplies a tooltip — e.g. the
   *  interactive folder-path breadcrumb on issue-comment chips (WHA-3774), where
   *  the two tooltips would otherwise overlap. Default true. */
  showTooltip?: boolean;
}

/**
 * Compact document chip for table cells.
 * Shows a file-type icon + truncated filename with tooltip.
 *
 * Visual specs:
 * - Height: 24px, border-radius: 4px
 * - Background: neutral-100, hover: neutral-200
 * - Icon: 14px, text: 12px, truncate when constrained
 * - Tooltip: full filename + "Open preview" (omitted when showTooltip is false)
 */
export function DocChip({
  filename,
  onClick,
  isLoading = false,
  className,
  showTooltip = true,
}: DocChipProps) {
  const { t } = useLanguage();
  const breadcrumbs = useDocBreadcrumbs();
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.();
  };

  const chipContent = (
    <>
      {isLoading ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-500" />
      ) : (
        <FileTypeIcon filename={filename} size={14} />
      )}
      <span className="min-w-0 truncate">{filename}</span>
    </>
  );
  const chipClass = cn(
    "inline-flex h-6 max-w-full items-center gap-1.5 rounded px-2",
    "bg-neutral-100 text-xs transition-colors",
    className
  );

  const button = onClick ? (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        chipClass,
        "text-neutral-700 hover:bg-neutral-200 active:bg-neutral-300",
        "focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-1",
        "disabled:cursor-wait disabled:opacity-70"
      )}
    >
      {chipContent}
    </button>
  ) : (
    <span className={cn(chipClass, "cursor-default text-neutral-500")}>{chipContent}</span>
  );

  if (!showTooltip) {
    return button;
  }

  // WHA-3829: inside the chat window a DocBreadcrumbsContext supplies the
  // document's resolved folder path — upgrade the hover to the same
  // interactive project → folder → filename breadcrumb the PM chips use.
  const segments = breadcrumbs?.getSegments(filename);
  if (breadcrumbs && segments && segments.length > 0) {
    return (
      <DocumentBreadcrumbHover segments={segments} buildingId={Number(breadcrumbs.buildingId)}>
        <span className="inline-flex max-w-full">{button}</span>
      </DocumentBreadcrumbHover>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-sm font-medium">{filename}</p>
          <p className="text-xs text-neutral-400">
            {onClick ? "Click to open preview" : t.pm.issues.detail.previewUnavailable}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface DocChipOverflowProps {
  /** Number of additional documents not shown */
  count: number;
  /** Click handler to show all documents */
  onClick: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * Overflow indicator chip showing "+N" for additional documents.
 */
export function DocChipOverflow({ count, onClick, className }: DocChipOverflowProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={cn(
              "inline-flex h-6 items-center justify-center rounded px-2",
              "bg-neutral-200 hover:bg-neutral-300 active:bg-neutral-400",
              "text-xs font-medium text-neutral-600 transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-1",
              className
            )}
          >
            +{count}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-sm">
            Show {count} more source{count > 1 ? "s" : ""}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface DocChipCollapsedProps {
  /** All filenames in the group */
  filenames: string[];
  /** Click handler for each document */
  onDocClick: (filename: string) => void;
  /** Filenames that should render with the loading spinner. */
  loadingFilenames?: Set<string>;
  /** Filenames that cannot be opened — listed as plain text instead of a
   *  button, so the list never offers a click that does nothing. */
  nonOpenableFilenames?: ReadonlySet<string>;
  /** Custom class name */
  className?: string;
}

/**
 * Collapsed document chip that shows the first filename + "+N" badge.
 * Clicking opens a popover dropdown listing all attached files.
 */
export function DocChipCollapsed({
  filenames,
  onDocClick,
  loadingFilenames,
  nonOpenableFilenames,
  className,
}: DocChipCollapsedProps) {
  const breadcrumbs = useDocBreadcrumbs();
  const [open, setOpen] = useState(false);
  const firstFilename = filenames[0];
  const extraCount = filenames.length - 1;
  const firstLoading = loadingFilenames?.has(firstFilename) ?? false;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Same bubble stop as DocChip's button: opening the file list must not
          // also fire the click handler of the row the chip sits in. Radix
          // composes its toggle after this handler, so the popover still opens.
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-6 max-w-full items-center gap-1.5 rounded px-2",
            "bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300",
            "text-xs text-neutral-700 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-1",
            className
          )}
        >
          {firstLoading ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-500" />
          ) : (
            <FileTypeIcon filename={firstFilename} size={14} />
          )}
          <span className="min-w-0 shrink truncate">{firstFilename}</span>
          <span className="ml-0.5 rounded bg-neutral-200 px-1 text-[10px] font-medium text-neutral-600">
            +{extraCount}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        // A portalled popover still bubbles its clicks through the React tree,
        // so picking a file here would also fire the click handler of whatever
        // row the chip sits in — DocChip stops the same bubble on its button.
        onClick={(e) => e.stopPropagation()}
        className="w-max min-w-56 max-w-[min(28rem,90vw)] p-1"
      >
        <div className="flex flex-col">
          {filenames.map((filename) => {
            const isLoading = loadingFilenames?.has(filename) ?? false;
            const rowContent = (
              <>
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-500" />
                ) : (
                  <FileTypeIcon filename={filename} size={14} />
                )}
                <span className="min-w-0 whitespace-normal break-all">{filename}</span>
              </>
            );
            // A file this surface can't open is listed, not offered.
            const row = nonOpenableFilenames?.has(filename) ? (
              <div
                key={filename}
                className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs text-neutral-500"
              >
                {rowContent}
              </div>
            ) : (
              <button
                key={filename}
                type="button"
                className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-neutral-100"
                onClick={() => {
                  onDocClick(filename);
                  setOpen(false);
                }}
              >
                {rowContent}
              </button>
            );
            // WHA-3829: same interactive folder-path hover as the standalone chip.
            const segments = breadcrumbs?.getSegments(filename);
            if (breadcrumbs && segments && segments.length > 0) {
              return (
                <DocumentBreadcrumbHover
                  key={filename}
                  segments={segments}
                  buildingId={Number(breadcrumbs.buildingId)}
                >
                  {row}
                </DocumentBreadcrumbHover>
              );
            }
            return row;
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface DocChipGroupProps {
  /** List of filenames to display */
  filenames: string[];
  /** Click handler for each document (receives filename) */
  onDocClick: (filename: string) => void;
  /** Maximum number of chips to show before overflow (default: 2) */
  maxVisible?: number;
  /** Callback when overflow chip is clicked (receives list of hidden filenames) */
  onOverflowClick?: (hiddenFilenames: string[]) => void;
  /** WHA-3330: filenames that should render with the loading spinner —
   *  e.g. chatbot attachments still mid-extraction at the time the
   *  user hit Send. Updated in place as the pipeline progresses. */
  loadingFilenames?: Set<string>;
  /** Custom class name */
  className?: string;
}

/**
 * Group of document chips with overflow handling.
 * Shows up to maxVisible chips, then a "+N" overflow indicator.
 */
export function DocChipGroup({
  filenames,
  onDocClick,
  maxVisible = 2,
  onOverflowClick,
  loadingFilenames,
  className,
}: DocChipGroupProps) {
  if (filenames.length === 0) return null;

  // Collapsed view for 3+ files
  if (filenames.length >= 3) {
    return (
      <div className={cn("flex flex-wrap items-center gap-1", className)}>
        <DocChipCollapsed
          filenames={filenames}
          onDocClick={onDocClick}
          loadingFilenames={loadingFilenames}
        />
      </div>
    );
  }

  const visibleFilenames = filenames.slice(0, maxVisible);
  const hiddenFilenames = filenames.slice(maxVisible);
  const hasOverflow = hiddenFilenames.length > 0;

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {visibleFilenames.map((filename) => (
        <DocChip
          key={filename}
          filename={filename}
          onClick={() => onDocClick(filename)}
          isLoading={loadingFilenames?.has(filename) ?? false}
        />
      ))}
      {hasOverflow && (
        <DocChipOverflow
          count={hiddenFilenames.length}
          onClick={() => onOverflowClick?.(hiddenFilenames)}
        />
      )}
    </div>
  );
}
